import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { CreditCard, ShieldCheck, Loader2, Lock, Flame, AlertTriangle, Landmark, Copy, CheckCircle2, ExternalLink, Gift, Check } from 'lucide-react';
import { toast } from 'sonner';
import { startPayment, startBankTransfer, startBankTransferWithCoupon } from '../services/api';
import { MACRO_SNAPSHOT_KEY } from './MacroPhase';
import { trackAddToCart, trackInitiateCheckout } from '../utils/barionPixel';
import { usePublicConfig } from '../utils/usePublicConfig';

const CheckoutModal = ({ open, onOpenChange, program, tierIndex = 0 }) => {
  const [form, setForm] = useState({
    name: '', email: '', address: '', zip: '', city: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [macroSnapshot, setMacroSnapshot] = useState(null);
  const { config } = usePublicConfig();
  const paymentsEnabled = config ? config.payments_enabled !== false : true;
  const paymentsDisabledMsg = (config && config.payments_disabled_message) ||
    'A kártyás fizetés átmenetileg nem elérhető — a Barion élesítésre várunk. Írj nekünk: info@oroszcoaching.hu';

  // Payment method — bank transfer coexists with Barion permanently (per user decision).
  // When card payments are disabled, default to bank transfer.
  const [method, setMethod] = useState('bank_transfer');
  useEffect(() => {
    setMethod(paymentsEnabled ? 'card' : 'bank_transfer');
  }, [paymentsEnabled, open]);

  // Bank transfer success payload (returned by startBankTransfer). When set,
  // the modal switches from the form view to a thank-you screen with the
  // account number, reference code, and "check your email" instructions.
  const [bankResult, setBankResult] = useState(null);
  useEffect(() => { if (!open) setBankResult(null); }, [open]);

  // Coupon code (Phase 3 retention). Prefilled from ?coupon= URL param on load
  // and persisted in localStorage so it survives navigation.
  const [couponCode, setCouponCode] = useState('');
  const [couponPreview, setCouponPreview] = useState(null);   // {discount_huf, final_amount_huf} | null
  const [couponError, setCouponError] = useState('');
  useEffect(() => {
    if (!open) return;
    try {
      const url = new URL(window.location.href);
      const fromUrl = url.searchParams.get('coupon');
      const fromStorage = localStorage.getItem('oc_pending_coupon');
      const initial = (fromUrl || fromStorage || '').toUpperCase();
      if (initial) setCouponCode(initial);
    } catch { /* ignore */ }
  }, [open]);
  const persistCoupon = (v) => {
    setCouponCode(v);
    setCouponPreview(null);
    setCouponError('');
    try { localStorage.setItem('oc_pending_coupon', v); } catch { /* ignore */ }
  };
  // Live validation on blur / debounced input. Shows the discounted price
  // BEFORE the visitor hits pay, per user request ("kapja meg a kupon kódot
  // a fizetés előtt hogy betudja írni").
  useEffect(() => {
    if (!open || !couponCode || !program) return undefined;
    const tierPrice = tier && tier.price ? tier.price : (program.price || '');
    const base = parseInt(String(tierPrice).replace(/[^\d]/g, ''), 10) || 0;
    if (!base) return undefined;
    const t = setTimeout(async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const { data } = await import('axios').then(m => m.default.post(
          `${API}/api/coupons/validate`,
          { code: couponCode.trim(), base_amount_huf: base },
        ));
        setCouponPreview(data);
        setCouponError('');
      } catch (e) {
        setCouponPreview(null);
        setCouponError(e?.response?.data?.detail || 'Érvénytelen kód.');
      }
    }, 450);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponCode, open, program?.id, tierIndex]);

  // Pick up the personalized macros saved by the Phase 2 calculator
  // (so they can be persisted with the payment + injected into the
  // post-purchase Welcome Email).
  useEffect(() => {
    if (!open) return;
    try {
      const raw = sessionStorage.getItem(MACRO_SNAPSHOT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.target_kcal) {
          setMacroSnapshot(parsed);
        }
      } else {
        setMacroSnapshot(null);
      }
    } catch {
      setMacroSnapshot(null);
    }
  }, [open]);

  const tier = (program && program.tiers && program.tiers[tierIndex]) || null;
  const headline = program ? (tier ? `${program.title} – ${tier.label}` : program.title) : '';
  const priceLabel = (tier && tier.price) || (program && program.price) || '';

  // Parse the "60.000 Ft / hó" style label into a numeric HUF value for the
  // Barion Full Pixel revenue field. Falls back to 0 on unparseable values.
  const numericValue = (() => {
    if (!priceLabel) return 0;
    const cleaned = String(priceLabel).replace(/[^\d]/g, '');
    return cleaned ? parseInt(cleaned, 10) : 0;
  })();

  // Fire `addToCart` once per modal open (program tier selected).
  useEffect(() => {
    if (!open || !program) return;
    trackAddToCart({
      programId:    program.id,
      programTitle: tier ? `${program.title} – ${tier.label}` : program.title,
      value:        numericValue,
      currency:     'HUF',
    });
  }, [open, program, tier, tierIndex, numericValue]);

  if (!program) return null;

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (method === 'card' && !paymentsEnabled) {
      toast.error(paymentsDisabledMsg);
      return;
    }
    if (!consent) {
      toast.error('A folytatáshoz el kell fogadnod az ÁSZF-et és az Adatkezelési tájékoztatót.');
      return;
    }
    setSubmitting(true);
    const billingPayload = {
      name:    form.name.trim(),
      email:   form.email.trim(),
      address: form.address.trim(),
      zip:     form.zip.trim(),
      city:    form.city.trim(),
      country: 'HU',
    };
    try {
      if (method === 'bank_transfer') {
        const res = await startBankTransferWithCoupon({
          program_id: program.id,
          tier_index: tierIndex,
          billing:    billingPayload,
        }, couponCode);
        // Clear the persisted coupon on success — it's spent.
        try { localStorage.removeItem('oc_pending_coupon'); } catch { /* ignore */ }
        setBankResult(res);
        setSubmitting(false);
        return;
      }

      // Card / Barion path
      trackInitiateCheckout({
        programId:    program.id,
        programTitle: headline,
        value:        numericValue,
        currency:     'HUF',
      });
      const res = await startPayment({
        program_id: program.id,
        tier_index: tierIndex,
        billing:    billingPayload,
        macro_snapshot: macroSnapshot ? {
          target_kcal:    Number(macroSnapshot.target_kcal),
          protein_g:      Number(macroSnapshot.protein_g),
          carbs_g:        Number(macroSnapshot.carbs_g),
          fat_g:          Number(macroSnapshot.fat_g),
          activity_label: macroSnapshot.activity_label || null,
          goal:           macroSnapshot.goal || null,
        } : null,
        source: macroSnapshot ? 'macro-calc' : 'direct',
        coupon_code: couponCode || undefined,
      });
      if (res?.gateway_url) {
        window.location.assign(res.gateway_url);
      } else {
        throw new Error('Hiányzó gateway URL');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Fizetés indítása sikertelen.';
      toast.error(typeof detail === 'string' ? detail : 'Próbáld újra.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="checkout-modal"
        className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 bg-white"
      >
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 md:p-8">
          <DialogHeader>
            <div className="flex items-center gap-2 text-yellow-400 text-xs font-bold tracking-widest uppercase mb-2">
              <CreditCard size={14} /> Biztonságos fizetés · Barion
            </div>
            <DialogTitle
              data-testid="checkout-title"
              className="text-2xl md:text-3xl font-bold text-white leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {headline}
            </DialogTitle>
            <DialogDescription className="text-yellow-400 text-xl md:text-2xl font-bold mt-2">
              {priceLabel}
            </DialogDescription>
          </DialogHeader>
        </div>

        {bankResult ? (
          <BankTransferSuccessView result={bankResult} onClose={() => onOpenChange(false)} />
        ) : (
        <form onSubmit={handleSubmit} data-testid="checkout-form" className="p-6 md:p-8 space-y-5">
          {/* Payment method selector */}
          <div data-testid="checkout-method-selector">
            <div className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">
              Fizetési mód
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="checkout-method-card"
                onClick={() => paymentsEnabled && setMethod('card')}
                disabled={!paymentsEnabled}
                className={
                  'relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 transition-all ' +
                  (method === 'card'
                    ? 'border-yellow-500 bg-yellow-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300') +
                  (!paymentsEnabled ? ' opacity-50 cursor-not-allowed' : ' cursor-pointer')
                }
              >
                <CreditCard size={18} className={method === 'card' ? 'text-yellow-700' : 'text-gray-500'} />
                <span className="text-xs font-bold text-gray-900">Bankkártya</span>
                <span className="text-[10px] text-gray-500">Barion · azonnali</span>
                {!paymentsEnabled && (
                  <span className="absolute -top-2 right-2 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">
                    hamarosan
                  </span>
                )}
              </button>
              <button
                type="button"
                data-testid="checkout-method-bank"
                onClick={() => setMethod('bank_transfer')}
                className={
                  'flex flex-col items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 transition-all cursor-pointer ' +
                  (method === 'bank_transfer'
                    ? 'border-yellow-500 bg-yellow-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300')
                }
              >
                <Landmark size={18} className={method === 'bank_transfer' ? 'text-yellow-700' : 'text-gray-500'} />
                <span className="text-xs font-bold text-gray-900">Banki utalás</span>
                <span className="text-[10px] text-gray-500">1-2 munkanap · aktiválás után</span>
              </button>
            </div>
          </div>

          {!paymentsEnabled && method === 'card' && (
            <div
              data-testid="checkout-payments-disabled-banner"
              className="rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 flex items-start gap-3"
              role="alert"
            >
              <AlertTriangle className="text-amber-700 flex-shrink-0 mt-0.5" size={20} />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-widest uppercase text-amber-800">
                  Kártyás fizetés átmenetileg szünetel
                </div>
                <p className="text-sm text-gray-900 mt-1 leading-relaxed">
                  {paymentsDisabledMsg}
                </p>
                <button
                  type="button"
                  onClick={() => setMethod('bank_transfer')}
                  className="mt-2 text-sm font-bold text-amber-800 underline hover:text-amber-900"
                >
                  Válts át banki utalásra →
                </button>
              </div>
            </div>
          )}

          {method === 'bank_transfer' && (
            <div
              data-testid="checkout-bank-transfer-info"
              className="rounded-xl border-2 border-sky-300 bg-sky-50 px-4 py-3 flex items-start gap-3"
            >
              <Landmark className="text-sky-700 flex-shrink-0 mt-0.5" size={20} />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-widest uppercase text-sky-800">
                  Hogyan működik az utalásos fizetés?
                </div>
                <p className="text-sm text-gray-900 mt-1 leading-relaxed">
                  Fiókot hozunk létre azonnal, kapsz egy emailt a bejelentkezéshez. Az utalás beérkezése
                  után (általában 1 munkanap) Gábor kézzel aktiválja a bérleted, és értesítünk emailben.
                </p>
              </div>
            </div>
          )}

          {macroSnapshot && (
            <div
              data-testid="checkout-macro-banner"
              className="rounded-xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 px-4 py-3 flex items-center gap-3"
            >
              <div className="shrink-0 w-10 h-10 rounded-full bg-yellow-100 grid place-items-center">
                <Flame size={18} className="text-yellow-700" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-widest uppercase text-yellow-700">
                  Phase 2 makrók átvitelre kerülnek
                </div>
                <div className="text-sm text-gray-900 font-semibold mt-0.5 leading-tight">
                  {Number(macroSnapshot.target_kcal).toLocaleString('hu-HU')} kcal · {macroSnapshot.protein_g}P · {macroSnapshot.carbs_g}C · {macroSnapshot.fat_g}F
                </div>
                <div className="text-[11px] text-gray-600 mt-0.5">
                  Az üdvözlő e-mailedbe automatikusan bekerülnek a vásárlás után.
                </div>
              </div>
            </div>
          )}

          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-1">
            Számlázási adatok
          </h3>
          <p className="text-xs text-gray-500 -mt-2 mb-3">
            Ezekkel az adatokkal állítjuk ki az elektronikus számlát, amit a Számlázz.hu automatikusan elküld neked.
          </p>

          <div>
            <Label htmlFor="ck-name" className="text-gray-700 font-semibold mb-1 block">Teljes név *</Label>
            <Input data-testid="checkout-name" id="ck-name" required value={form.name}
                   onChange={update('name')} className="h-11" placeholder="Pl. Kovács János" />
          </div>

          <div>
            <Label htmlFor="ck-email" className="text-gray-700 font-semibold mb-1 block">E-mail *</Label>
            <Input data-testid="checkout-email" id="ck-email" type="email" required value={form.email}
                   onChange={update('email')} className="h-11" placeholder="janos@example.com" />
            <p className="text-xs text-gray-500 mt-1">Erre a címre érkezik a számla és a visszaigazolás.</p>
          </div>

          <div>
            <Label htmlFor="ck-address" className="text-gray-700 font-semibold mb-1 block">Cím (utca, házszám) *</Label>
            <Input data-testid="checkout-address" id="ck-address" required value={form.address}
                   onChange={update('address')} className="h-11" placeholder="Pl. Kossuth utca 12." />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="min-w-0">
              <Label htmlFor="ck-zip" className="text-gray-700 font-semibold mb-1 block">Irányítószám *</Label>
              <Input data-testid="checkout-zip" id="ck-zip" required value={form.zip}
                     onChange={update('zip')} className="h-11" placeholder="3530" />
            </div>
            <div className="min-w-0 sm:col-span-2">
              <Label htmlFor="ck-city" className="text-gray-700 font-semibold mb-1 block">Város *</Label>
              <Input data-testid="checkout-city" id="ck-city" required value={form.city}
                     onChange={update('city')} className="h-11" placeholder="Miskolc" />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
            <ShieldCheck className="text-yellow-700 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-xs text-gray-700 leading-relaxed">
              A fizetést a <strong>Barion</strong> biztonságos felülete kezeli. Sikeres tranzakció
              után a <strong>Számlázz.hu</strong> automatikusan elküldi az elektronikus számlát az
              általad megadott e-mail címre.
            </p>
          </div>

          {/* GDPR / Barion consent — required before payment */}
          <div className="flex items-start gap-3 pt-1">
            <Checkbox
              id="ck-consent"
              data-testid="checkout-consent-checkbox"
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              className="mt-0.5 border-gray-400 data-[state=checked]:bg-yellow-600 data-[state=checked]:border-yellow-600"
            />
            <Label
              htmlFor="ck-consent"
              data-testid="checkout-consent-label"
              className="text-xs text-gray-700 leading-relaxed font-normal cursor-pointer"
            >
              ✓ Elfogadom az{' '}
              <a href="/aszf" target="_blank" rel="noopener noreferrer"
                 className="underline text-yellow-700 hover:text-yellow-600 font-semibold">ÁSZF</a>-et és az{' '}
              <a href="/adatvedelem" target="_blank" rel="noopener noreferrer"
                 className="underline text-yellow-700 hover:text-yellow-600 font-semibold">Adatkezelési tájékoztatót</a>
              , és tudomásul veszem a Barion adattovábbítási nyilatkozatot.
            </Label>
          </div>

          {/* Trust badge row — subtle conversion booster */}
          <div
            data-testid="checkout-trust-badges"
            className="flex flex-col items-center gap-3 border-y border-gray-100 py-4"
          >
            <img
              data-testid="checkout-barion-banner"
              src="/assets/barion/barion-smart-banner-light.svg"
              alt="Elfogadott fizetési módok: Visa, Mastercard, Maestro, Apple Pay, Google Pay – Barion"
              className="h-9 md:h-10 w-auto"
              loading="lazy"
            />
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-gray-600 font-medium tracking-wide">
              <span className="inline-flex items-center gap-1.5">
                <Lock size={12} className="text-yellow-600" /> SSL-titkosított
              </span>
              <span className="text-gray-300 select-none">·</span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden className="leading-none">🇭🇺</span> Magyar fizetési szolgáltató
              </span>
              <span className="text-gray-300 select-none">·</span>
              <span className="inline-flex items-center gap-1.5">
                <CreditCard size={12} className="text-yellow-600" /> Bankkártya / Apple Pay / Google Pay
              </span>
            </div>
          </div>

          {/* Coupon code — works for both bank transfer AND Barion card. Live
              validation shows the discounted price before the visitor commits. */}
          <details data-testid="checkout-coupon-toggle" className="rounded-xl border border-gray-200 bg-gray-50" open={!!couponCode}>
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-gray-700 flex items-center gap-2 select-none">
              <Gift size={16} className="text-yellow-700" />
              Van kuponkódom
            </summary>
            <div className="px-4 pb-4 pt-1">
              <Input
                data-testid="checkout-coupon-input"
                value={couponCode}
                onChange={(e) => persistCoupon(e.target.value.toUpperCase())}
                placeholder="pl. SUMMER25 vagy LOYAL10-XXXX"
                className="uppercase font-mono tracking-wider"
              />
              {couponPreview && (
                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 flex items-center gap-2" data-testid="checkout-coupon-preview">
                  <Check size={14} className="text-emerald-600" />
                  <div>
                    <div className="font-bold">Kupon érvényes!</div>
                    <div className="text-xs">
                      Kedvezmény: <strong>–{couponPreview.discount_huf?.toLocaleString('hu-HU')} Ft</strong>
                      {' · '}Fizetendő: <strong>{couponPreview.final_amount_huf?.toLocaleString('hu-HU')} Ft</strong>
                    </div>
                  </div>
                </div>
              )}
              {couponError && (
                <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800" data-testid="checkout-coupon-error">
                  {couponError}
                </div>
              )}
              {!couponPreview && !couponError && (
                <p className="text-[11px] text-gray-500 mt-1.5">
                  Az edzőtől kapott kuponkódot ide írd be. A kedvezmény mind utaláskor, mind bankkártyás fizetésnél levonódik.
                </p>
              )}
            </div>
          </details>

          <Button
            data-testid="checkout-submit-btn"
            type="submit"
            disabled={submitting || !consent || (method === 'card' && !paymentsEnabled)}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-lg py-6 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              method === 'bank_transfer'
                ? <><Loader2 className="mr-2 animate-spin" size={20} /> Rendelés rögzítése…</>
                : <><Loader2 className="mr-2 animate-spin" size={20} /> Átirányítás a Barion oldalára…</>
            ) : method === 'bank_transfer' ? (
              <>Utalási adatok kérése →</>
            ) : !paymentsEnabled ? (
              <>Kártyás fizetés átmenetileg nem elérhető</>
            ) : (
              <>Tovább a biztonságos fizetéshez →</>
            )}
          </Button>

          <p className="text-[11px] text-gray-500 text-center">
            {method === 'bank_transfer'
              ? 'A gomb megnyomása után emailt küldünk a fiókod aktiválásához és az utalási adatokhoz.'
              : 'A fizetés Barion sandbox környezetben fut a tesztidőszak alatt.'}
          </p>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// -------------------------------------------------------------------------
// Bank transfer success view — shown INSIDE the checkout modal after the
// backend confirms account creation + pending pass.
// -------------------------------------------------------------------------
const BankTransferSuccessView = ({ result, onClose }) => {
  const [copied, setCopied] = useState(null);
  const copy = async (label, val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  };
  const rows = [
    { label: 'Bank',          value: result.bank            },
    { label: 'Számlaszám',    value: result.account_number  },
    { label: 'Kedvezményezett', value: result.account_holder },
    { label: 'Összeg',        value: `${Number(result.amount_huf || 0).toLocaleString('hu-HU')} Ft` },
    { label: 'Közlemény',     value: result.reference_code, highlight: true },
  ];
  const hasDiscount = result.discount_pct && Number(result.discount_huf || 0) > 0;
  return (
    <div data-testid="checkout-bank-success" className="p-6 md:p-8 space-y-6">
      <div className="text-center">
        <div className="inline-flex w-14 h-14 rounded-full bg-emerald-100 items-center justify-center mb-3">
          <CheckCircle2 className="text-emerald-700" size={28} />
        </div>
        <h3 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Sikeres rendelés — kövesd az emailedet!
        </h3>
        <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
          Létrehoztunk neked egy fiókot és emailt küldtünk a jelszó beállításához. Az utalást
          az alábbi adatokra kérjük.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 divide-y divide-gray-200">
        {hasDiscount && (
          <div data-testid="checkout-bank-discount" className="flex items-center justify-between gap-3 px-4 py-3 bg-yellow-50">
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-yellow-800">Kupon</div>
              <div className="mt-0.5 text-sm text-gray-900">
                {result.discount_pct}% kedvezmény ·{' '}
                <span className="line-through text-gray-500">{Number(result.original_amount_huf || 0).toLocaleString('hu-HU')} Ft</span>{' '}
                → <strong>{Number(result.amount_huf || 0).toLocaleString('hu-HU')} Ft</strong>
                <span className="text-emerald-700 ml-1">(–{Number(result.discount_huf).toLocaleString('hu-HU')} Ft)</span>
              </div>
            </div>
          </div>
        )}
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-3 px-4 py-3">
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-gray-500">{r.label}</div>
              <div className={`mt-0.5 text-sm ${r.highlight ? 'font-mono font-bold text-lg text-gray-900' : 'text-gray-900'}`}>
                {r.value}
              </div>
            </div>
            <button
              type="button"
              onClick={() => copy(r.label, r.value)}
              data-testid={`checkout-bank-copy-${r.label}`}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-yellow-700 bg-white border border-gray-300 rounded-md px-2 py-1 transition-colors"
            >
              {copied === r.label ? <CheckCircle2 size={12} className="text-emerald-600" /> : <Copy size={12} />}
              {copied === r.label ? 'másolva' : 'másol'}
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>Fontos:</strong> A közlemény mezőbe pontosan ezt írd:{' '}
        <span className="font-mono font-bold">{result.reference_code}</span>. Ez alapján
        tudjuk beazonosítani az utalásod és aktiválni a bérleted.
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          data-testid="checkout-bank-goto-portal"
          onClick={() => window.location.href = result.portal_login_url || '/fiokom/bejelentkezes'}
          className="flex-1 bg-gray-900 hover:bg-yellow-600 text-white font-bold"
        >
          Ugrás a Fiókomhoz <ExternalLink size={14} className="ml-2" />
        </Button>
        <Button
          data-testid="checkout-bank-close"
          onClick={onClose}
          variant="outline"
          className="flex-1 border-gray-300"
        >
          Bezárás
        </Button>
      </div>
    </div>
  );
};

export default CheckoutModal;
